"use client";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils/utils";

interface TextMarqueeProps {
  items: string[];
  /** Seconds for one full group to travel past — speed, not total loop time. */
  duration?: number;
  className?: string;
  itemClassName?: string;
}

// Endless text ribbon. InfiniteMarquee is image-only, so word strips use this.
//
// The seam is invisible only if TWO things hold, and the naive "render it twice,
// translate -50%" version gets the second one wrong:
//
//  1. The shift equals exactly one group's width — including the trailing gap,
//     which is why the spacing lives in the group's own `pr-6` rather than as a
//     `gap` on the track. A gap between groups would not be part of the measured
//     width, and the loop would drift by that amount every cycle.
//
//  2. The track stays wider than the viewport for the whole cycle. With only two
//     copies and a group narrower than the screen, the track runs out before the
//     loop restarts — you get blank space, then the text snaps back into view.
//     So the group is measured and repeated enough times to cover the viewport
//     plus one spare group to slide in.
const TextMarquee = ({
  items,
  duration = 22,
  className,
  itemClassName,
}: TextMarqueeProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const [copies, setCopies] = useState(2);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const viewport = viewportRef.current;
    const group = groupRef.current;
    if (!viewport || !group) return;

    // ResizeObserver fires once on observe() with the current size, so the first
    // measurement arrives through this callback — no synchronous setState in the
    // effect body. It also re-runs when the webfont swaps in and the text
    // reflows, which is the other thing that quietly breaks a fixed copy count.
    const observer = new ResizeObserver(() => {
      const groupWidth = group.offsetWidth;
      if (!groupWidth) return;
      setCopies(Math.max(2, Math.ceil(viewport.offsetWidth / groupWidth) + 1));
    });

    observer.observe(viewport);
    observer.observe(group);
    return () => observer.disconnect();
  }, []);

  // Shift by exactly one group, whatever the copy count is, so the speed stays
  // constant as copies change.
  const shift = `-${100 / copies}%`;

  return (
    <div
      ref={viewportRef}
      className={cn("relative flex overflow-hidden", className)}
    >
      <motion.div
        className="flex shrink-0"
        animate={reduceMotion ? undefined : { x: ["0%", shift] }}
        transition={{ duration, repeat: Infinity, ease: "linear" }}
      >
        {Array.from({ length: copies }).map((_, copy) => (
          <div
            key={copy}
            ref={copy === 0 ? groupRef : undefined}
            // Trailing pr-6 matches the inner gap so the group's measured width
            // includes the space that follows it — see note 1 above.
            className="flex shrink-0 items-center gap-6 pr-6"
            // Only the first group is real content; the rest are visual repeats
            // and would otherwise be announced over and over.
            aria-hidden={copy > 0}
          >
            {items.map((label, i) => (
              <span
                key={i}
                className={cn(
                  "flex shrink-0 items-center gap-6 whitespace-nowrap text-2xl font-black uppercase tracking-tight text-accent-foreground/90 md:text-3xl",
                  itemClassName,
                )}
              >
                {label}
                <CalendarClock size={20} className="text-primary" />
              </span>
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default TextMarquee;
